import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { motion } from "framer-motion";
import { List, X, SignOut, User } from "@phosphor-icons/react";

export default function Navbar({ variant = "marketing" }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Close mobile menu when the route changes / when escape is pressed
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    }, [open]);

    const isLanding = variant === "marketing";
    const transparent = isLanding && !scrolled;

    // Smart click handler for in-page anchors (#features, #how, #pricing).
    // If we're already on "/" → smooth-scroll to the target section.
    // If we're on another route → navigate to "/<anchor>" so the browser jumps.
    const handleAnchorClick = (anchor) => (e) => {
        const id = anchor.replace(/^#/, "");
        if (loc.pathname === "/") {
            e.preventDefault();
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                window.history.replaceState(null, "", "/" + anchor);
            }
        }
        // Otherwise let the default <a href="/#foo"> behaviour run after route change.
        setOpen(false);
    };

    // Logo click — if already on "/", scroll to top instead of doing nothing.
    const handleLogoClick = (e) => {
        if (loc.pathname === "/") {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
        setOpen(false);
    };

    const linkClass = `text-sm font-semibold uppercase tracking-[0.18em] whitespace-nowrap transition-colors ${
        transparent ? "text-white/90 hover:text-accent" : "text-primary hover:text-accent"
    }`;

    return (
        <motion.header
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
                transparent ? "bg-transparent" : "bg-background/90 backdrop-blur-xl border-b border-border"
            }`}
            data-testid="main-navbar"
        >
            <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-24 h-16 sm:h-20 flex items-center justify-between gap-3">
                <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2 sm:gap-3 group shrink-0" data-testid="brand-logo">
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-md flex items-center justify-center shadow-sm transition-colors ${transparent ? "bg-accent text-primary ring-1 ring-white/30" : "bg-primary text-primary-foreground"}`}>
                        <span className="font-heading text-lg sm:text-xl font-bold leading-none">cf</span>
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className={`font-heading text-base sm:text-xl tracking-tight transition-colors ${transparent ? "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" : "text-primary"}`}>CharityFundraiser</span>
                        <span className={`label-eyebrow text-[9px] sm:text-[10px] mt-0.5 sm:mt-1 transition-colors hidden sm:block ${transparent ? "text-accent drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" : ""}`}>Elite Fundraising Platform</span>
                    </div>
                </Link>

                {/* Desktop nav — only at lg (1024px) and up to ensure room for 5 links + 2 CTAs */}
                <nav className="hidden lg:flex items-center gap-6 xl:gap-9 ml-auto">
                    {isLanding && (
                        <>
                            <Link to="/features" className={linkClass} data-testid="nav-features">Features</Link>
                            <Link to="/problems-we-solve" className={linkClass} data-testid="nav-problems">Why us</Link>
                            <a href={loc.pathname === "/" ? "#how" : "/#how"} onClick={handleAnchorClick("#how")} className={linkClass} data-testid="nav-how">How it works</a>
                            <Link to="/tour" className={linkClass} data-testid="nav-tour">Live tour</Link>
                            <Link to="/demo" className={linkClass} data-testid="nav-demo">Try demo</Link>
                            <Link to="/find-an-auctioneer" className={`${linkClass} inline-flex items-center gap-1.5`} data-testid="nav-find-auctioneer">
                                <span>Find Auctioneers</span>
                                <span className="text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 font-bold tracking-wider">8,800+</span>
                            </Link>
                            <Link to="/compare" className={linkClass} data-testid="nav-compare">Compare</Link>
                            <Link to="/blog" className={linkClass} data-testid="nav-blog">Fundraising News</Link>
                            <a href={loc.pathname === "/" ? "#pricing" : "/#pricing"} onClick={handleAnchorClick("#pricing")} className={linkClass} data-testid="nav-pricing">Pricing</a>
                        </>
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-4 shrink-0">
                    {user ? (
                        <>
                            <Link
                                to={user.role === "super_admin" ? "/super" : user.role === "org_admin" ? "/admin" : "/dashboard"}
                                className={`${linkClass} flex items-center gap-2`}
                                data-testid="nav-dashboard"
                            >
                                <User size={16} weight="regular" />
                                {user.name.split(" ")[0]}
                            </Link>
                            <button onClick={() => { logout(); nav("/"); }} className={`btn-ghost ${transparent ? "text-white hover:bg-white/10" : ""}`} data-testid="nav-logout">
                                <SignOut size={14} /> Logout
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className={linkClass} data-testid="nav-login">Sign in</Link>
                            <Link to="/signup-org" className="btn-accent whitespace-nowrap" data-testid="nav-cta">Start fundraising</Link>
                        </>
                    )}
                </div>

                {/* Hamburger — visible below lg (so tablet + mobile both get it) */}
                <button
                    className={`lg:hidden p-2 rounded-md transition-colors ${transparent ? "text-white hover:bg-white/10" : "text-primary hover:bg-primary/10"}`}
                    onClick={() => setOpen(!open)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={26} weight="bold" /> : <List size={26} weight="bold" />}
                </button>
            </div>

            {/* Mobile + tablet drawer */}
            {open && (
                <div
                    className="lg:hidden bg-background border-t border-border px-6 py-6 flex flex-col gap-1 shadow-xl max-h-[calc(100vh-4rem)] overflow-y-auto"
                    data-testid="mobile-menu"
                >
                    {isLanding && (
                        <>
                            <Link to="/features" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>Features</Link>
                            <Link to="/problems-we-solve" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-problems">Why us</Link>
                            <a href={loc.pathname === "/" ? "#how" : "/#how"} onClick={handleAnchorClick("#how")} className="block py-3 label-eyebrow border-b border-border">How it works</a>
                            <Link to="/tour" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>Live tour</Link>
                            <Link to="/find-an-auctioneer" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-find-auctioneer">
                                Find Auctioneers
                                <span className="ml-2 text-[10px] bg-accent text-accent-foreground px-1.5 py-0.5 font-bold tracking-wider">8,800+</span>
                            </Link>
                            <Link to="/compare" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)} data-testid="mobile-nav-compare">Compare</Link>
                            <Link to="/blog" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>Fundraising News</Link>
                            <a href={loc.pathname === "/" ? "#pricing" : "/#pricing"} onClick={handleAnchorClick("#pricing")} className="block py-3 label-eyebrow border-b border-border">Pricing</a>
                            <Link to="/contact" className="block py-3 label-eyebrow border-b border-border" onClick={() => setOpen(false)}>Contact</Link>
                        </>
                    )}
                    <div className="mt-4 flex flex-col gap-3">
                        {user ? (
                            <>
                                <Link to={user.role === "super_admin" ? "/super" : user.role === "org_admin" ? "/admin" : "/dashboard"} className="btn-ghost text-center" onClick={() => setOpen(false)}>Dashboard</Link>
                                <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="btn-ghost text-center">Logout</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="btn-ghost text-center" onClick={() => setOpen(false)}>Sign in</Link>
                                <Link to="/signup-org" className="btn-accent text-center" onClick={() => setOpen(false)}>Start fundraising</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </motion.header>
    );
}
